import React, { useState, useEffect, useCallback } from 'react';
import styles from './LifecyclePanel.module.css';
import api, { fetchHistoricoCliente } from '../../services/api';
import ClientHistoryModal from '../Modals/ClientHistoryModal';

// Configuração dos grupos Lifecycle
const GRUPOS_CONFIG = {
    champion: { emoji: '💎', label: 'Campeão', cor: '#FFD700', corTexto: '#92700c', desc: 'Clientes top: compram frequentemente, ticket alto e recentes' },
    loyal: { emoji: '⭐', label: 'Leal', cor: '#28a745', corTexto: '#1a6b2d', desc: 'Clientes fiéis: compram com regularidade nos últimos 30 dias' },
    new: { emoji: '🌱', label: 'Novo', cor: '#17a2b8', corTexto: '#0e6977', desc: 'Primeira compra recente: fez 1 pedido nos últimos 20 dias' },
    at_risk: { emoji: '🔥', label: 'Em Risco', cor: '#fd7e14', corTexto: '#b35a0e', desc: 'Já compraram mas estão inativos entre 30 e 90 dias' },
    alerta: { emoji: '💀', label: 'Alerta', cor: '#dc3545', corTexto: '#8b1a25', desc: 'Inativos há mais de 90 dias — risco alto de perda definitiva' }
};

const OPCOES_MESES = [
    { value: '', label: 'Todos os períodos' },
    { value: '2', label: '2+ meses' },
    { value: '3', label: '3+ meses' },
    { value: '6', label: '6+ meses' },
    { value: '12', label: '1+ ano' },
    { value: '24', label: '2+ anos' },
];

const getScoreColor = (score) => {
    if (score <= 25) return '#28a745';
    if (score <= 50) return '#ffc107';
    if (score <= 75) return '#fd7e14';
    return '#dc3545';
};

const fmtMoeda = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtData = (d) => {
    if (!d) return '—';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); }
    catch { return d; }
};

const LifecyclePanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [grupoAtivo, setGrupoAtivo] = useState(null);
    const [ordenarPor, setOrdenarPor] = useState('churn_score');
    const [ordem, setOrdem] = useState('desc');
    const [tipoLead, setTipoLead] = useState('');
    const [tipoPessoa, setTipoPessoa] = useState('');
    const [mesesInativo, setMesesInativo] = useState('');
    const [valorMin, setValorMin] = useState('');
    const [valorMax, setValorMax] = useState('');
    const [estado, setEstado] = useState('');
    const [busca, setBusca] = useState('');
    const [buscaInput, setBuscaInput] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);

    // Modal de histórico
    const [showModal, setShowModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [historico, setHistorico] = useState(null);
    const [loadingHistorico, setLoadingHistorico] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setBusca(buscaInput), 400);
        return () => clearTimeout(timer);
    }, [buscaInput]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (grupoAtivo) params.set('grupo', grupoAtivo);
            if (tipoLead) params.set('tipoLead', tipoLead);
            if (tipoPessoa) params.set('tipoPessoa', tipoPessoa);
            if (mesesInativo) params.set('mesesInativo', mesesInativo);
            if (valorMin) params.set('valorMin', valorMin);
            if (valorMax) params.set('valorMax', valorMax);
            if (estado) params.set('estado', estado);
            if (busca) params.set('busca', busca);
            params.set('ordenarPor', ordenarPor);
            params.set('ordem', ordem);
            params.set('page', page);
            params.set('limit', limit);
            const response = await api.get(`/api/clientes/lifecycle?${params.toString()}`);
            setData(response.data);
        } catch (err) {
            console.error('Erro ao buscar lifecycle:', err);
            setError(err.message || 'Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    }, [grupoAtivo, ordenarPor, ordem, tipoLead, tipoPessoa, mesesInativo, valorMin, valorMax, estado, busca, page, limit]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [grupoAtivo, tipoLead, tipoPessoa, mesesInativo, valorMin, valorMax, estado, busca, ordenarPor, ordem]);

    const filtrosAtivos = [grupoAtivo, tipoLead, tipoPessoa, mesesInativo, valorMin, valorMax, estado, busca].filter(Boolean).length;

    const limparFiltros = () => {
        setGrupoAtivo(null);
        setTipoLead('');
        setTipoPessoa('');
        setMesesInativo('');
        setValorMin('');
        setValorMax('');
        setEstado('');
        setBusca('');
        setBuscaInput('');
    };

    const handleSort = (campo) => {
        if (ordenarPor === campo) {
            setOrdem(o => o === 'desc' ? 'asc' : 'desc');
        } else {
            setOrdenarPor(campo);
            setOrdem('desc');
        }
    };

    const sortIcon = (campo) => {
        if (ordenarPor !== campo) return ' ↕';
        return ordem === 'desc' ? ' ↓' : ' ↑';
    };

    // Exportar TODOS os dados para CSV (busca da API sem limite de paginação)
    const [exportando, setExportando] = useState(false);
    const exportarCSV = async () => {
        setExportando(true);
        try {
            // Buscar TODOS os clientes com os mesmos filtros, sem paginação
            const params = new URLSearchParams();
            if (grupoAtivo) params.set('grupo', grupoAtivo);
            if (tipoLead) params.set('tipoLead', tipoLead);
            if (tipoPessoa) params.set('tipoPessoa', tipoPessoa);
            if (mesesInativo) params.set('mesesInativo', mesesInativo);
            if (valorMin) params.set('valorMin', valorMin);
            if (valorMax) params.set('valorMax', valorMax);
            if (estado) params.set('estado', estado);
            if (busca) params.set('busca', busca);
            params.set('ordenarPor', ordenarPor);
            params.set('ordem', ordem);
            params.set('page', 1);
            params.set('limit', 99999); // Buscar tudo

            const response = await api.get(`/api/clientes/lifecycle?${params.toString()}`);
            const todosClientes = response.data?.clientes || [];

            if (!todosClientes.length) return;

            const headers = ['Nome', 'Telefone', 'Email', 'CPF/CNPJ', 'Cidade', 'UF', 'Grupo', 'Score Churn', 'Dias Inativo', 'Compras', 'Valor Total', 'Ticket Médio', 'Última Compra', 'Tipo Lead'];
            const rows = todosClientes.map(c => [
                c.nome || '',
                c.telefone || '',
                c.email || '',
                c.cpf_cnpj || '',
                c.cidade || '',
                c.uf || '',
                GRUPOS_CONFIG[c.grupo_lifecycle]?.label || c.grupo_lifecycle,
                c.churn_score,
                c.dias_inativo !== null ? c.dias_inativo : 'Nunca',
                c.qtd_compras,
                (c.valor_total || 0).toFixed(2).replace('.', ','),
                (c.ticket_medio || 0).toFixed(2).replace('.', ','),
                c.ultima_compra || '',
                c.tipo_lead || ''
            ]);

            const csvContent = [headers, ...rows]
                .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
                .join('\n');

            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const grupo = grupoAtivo ? `_${GRUPOS_CONFIG[grupoAtivo]?.label || grupoAtivo}` : '';
            link.download = `lifecycle${grupo}_${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Erro ao exportar:', err);
        } finally {
            setExportando(false);
        }
    };

    const abrirWhatsApp = (cliente) => {
        const tel = (cliente.telefone || '').replace(/\D/g, '');
        if (!tel) return;
        const num = tel.startsWith('55') ? tel : `55${tel}`;
        window.open(`https://wa.me/${num}`, '_blank');
    };

    // Abrir modal de histórico
    const handleVerHistorico = async (cliente) => {
        setSelectedCliente({
            contato_id: cliente.cliente_id,
            contato_nome: cliente.nome,
            celular: cliente.telefone,
            telefone: cliente.telefone,
            email: cliente.email,
            cpf_cnpj: cliente.cpf_cnpj,
            cidade: cliente.cidade,
            ultima_compra: cliente.ultima_compra,
            total_gasto: cliente.valor_total,
            dias_sem_comprar: cliente.dias_inativo
        });
        setShowModal(true);
        setLoadingHistorico(true);
        setHistorico(null);

        try {
            const response = await fetchHistoricoCliente({
                id: cliente.cliente_id,
                nome: cliente.nome
            });
            if (response.success) {
                setHistorico(response.data);
            }
        } catch (err) {
            console.error('Erro ao buscar histórico:', err);
        } finally {
            setLoadingHistorico(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCliente(null);
        setHistorico(null);
    };

    // --- Renderização ---
    if (loading && !data) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <span>Carregando dados lifecycle...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.emptyState}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>⚠️</div>
                <div>Erro ao carregar: {error}</div>
                <button onClick={fetchData} className={styles.btnOutline} style={{ marginTop: 12 }}>
                    🔄 Tentar novamente
                </button>
            </div>
        );
    }

    if (!data || !data.kpis) {
        return (
            <div className={styles.emptyState}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>📭</div>
                <div>Nenhum dado de cliente encontrado</div>
            </div>
        );
    }

    const { kpis, clientes, pagination, resumo, estados_disponiveis } = data;
    const gruposOrdem = ['champion', 'loyal', 'new', 'at_risk', 'alerta'];

    return (
        <div className={styles.container}>
            {/* === Resumo Geral (estilo Recuperação) === */}
            <div className={styles.resumoCards}>
                <div className={styles.resumoCard}>
                    <div className={styles.resumoValor}>{resumo?.total_clientes?.toLocaleString('pt-BR') || 0}</div>
                    <div className={styles.resumoLabel}>Total Clientes</div>
                </div>
                <div className={styles.resumoCard}>
                    <div className={styles.resumoValor}>{fmtMoeda(resumo?.ticket_medio_global)}</div>
                    <div className={styles.resumoLabel}>Ticket Médio</div>
                </div>
                <div className={styles.resumoCard}>
                    <div className={styles.resumoValor} style={{ color: getScoreColor(resumo?.churn_medio || 0) }}>
                        {resumo?.churn_medio || 0}/100
                    </div>
                    <div className={styles.resumoLabel}>Churn Médio</div>
                </div>
            </div>

            {/* === Cards dos Grupos Lifecycle === */}
            <div className={styles.gruposGrid}>
                {gruposOrdem.map(gId => {
                    const kpi = kpis[gId];
                    const cfg = GRUPOS_CONFIG[gId];
                    if (!kpi || !cfg) return null;
                    const isActive = grupoAtivo === gId;

                    return (
                        <div
                            key={gId}
                            className={`${styles.grupoCard} ${isActive ? styles.grupoCardActive : ''}`}
                            onClick={() => setGrupoAtivo(prev => prev === gId ? null : gId)}
                            style={{
                                borderColor: isActive ? cfg.cor : 'transparent',
                                '--grupo-cor': cfg.cor
                            }}
                        >
                            <div className={styles.tooltip}>{cfg.desc}</div>
                            <div className={styles.grupoHeader}>
                                <span className={styles.grupoEmoji}>{cfg.emoji}</span>
                                <span className={styles.grupoLabel} style={{ color: cfg.corTexto }}>{cfg.label}</span>
                            </div>
                            <div className={styles.grupoTotal} style={{ color: cfg.corTexto }}>{kpi.total.toLocaleString('pt-BR')}</div>
                            <div className={styles.grupoMeta}>{fmtMoeda(kpi.valor_total)}</div>
                            <div className={styles.grupoChurn}>
                                <div className={styles.churnBarTrack}>
                                    <div className={styles.churnBarFill} style={{ width: `${kpi.churn_medio}%`, background: getScoreColor(kpi.churn_medio) }}></div>
                                </div>
                                <span style={{ color: getScoreColor(kpi.churn_medio), fontSize: '0.65rem', fontWeight: 700 }}>{kpi.churn_medio}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* === Barra de Filtros Inline === */}
            <div className={styles.filtersBar}>
                <div className={styles.searchBox}>
                    <span className={styles.searchIcon}>🔎</span>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Buscar cliente (nome, CPF/CNPJ ou e-mail)..."
                        value={buscaInput}
                        onChange={e => setBuscaInput(e.target.value)}
                    />
                </div>
                <button
                    className={`${styles.btnFilter} ${mostrarFiltros ? styles.btnFilterActive : ''}`}
                    onClick={() => setMostrarFiltros(p => !p)}
                >
                    🎛️ Filtros {filtrosAtivos > 0 && <span className={styles.filterCount}>{filtrosAtivos}</span>}
                </button>
                {filtrosAtivos > 0 && (
                    <button className={styles.btnClear} onClick={limparFiltros}>✕ Limpar</button>
                )}
                <button className={styles.btnExport} onClick={exportarCSV} disabled={exportando || !data?.clientes?.length} title="Exportar todos os dados filtrados para CSV">
                    {exportando ? '⏳ Exportando...' : '📥 Exportar'}
                </button>
            </div>

            {/* === Filtros Expandidos === */}
            {mostrarFiltros && (
                <div className={styles.filtersPanel}>
                    <div className={styles.filtersGrid}>
                        <div className={styles.filterItem}>
                            <label>⏰ Inativo há</label>
                            <select value={mesesInativo} onChange={e => setMesesInativo(e.target.value)}>
                                {OPCOES_MESES.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterItem}>
                            <label>💰 Valor Mín</label>
                            <input type="number" placeholder="0" value={valorMin} onChange={e => setValorMin(e.target.value)} min="0" step="50" />
                        </div>
                        <div className={styles.filterItem}>
                            <label>💰 Valor Máx</label>
                            <input type="number" placeholder="Sem limite" value={valorMax} onChange={e => setValorMax(e.target.value)} min="0" step="50" />
                        </div>
                        <div className={styles.filterItem}>
                            <label>🗺️ Estado</label>
                            <select value={estado} onChange={e => setEstado(e.target.value)}>
                                <option value="">Todos</option>
                                {(estados_disponiveis || []).map(uf => (
                                    <option key={uf} value={uf}>{uf}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterItem}>
                            <label>🪪 Documento</label>
                            <select value={tipoPessoa} onChange={e => setTipoPessoa(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="pf">PF (CPF)</option>
                                <option value="pj">PJ (CNPJ)</option>
                            </select>
                        </div>
                        <div className={styles.filterItem}>
                            <label>🏷️ Lead</label>
                            <select value={tipoLead} onChange={e => setTipoLead(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="prospect">Prospect</option>
                                <option value="novo">Novo</option>
                                <option value="recorrente">Recorrente</option>
                                <option value="vip">VIP</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* === Badges Filtros Ativos === */}
            {filtrosAtivos > 0 && (
                <div className={styles.activeBadges}>
                    {grupoAtivo && (
                        <span className={styles.badge}>
                            {GRUPOS_CONFIG[grupoAtivo]?.emoji} {GRUPOS_CONFIG[grupoAtivo]?.label}
                            <button onClick={() => setGrupoAtivo(null)}>✕</button>
                        </span>
                    )}
                    {mesesInativo && <span className={styles.badge}>⏰ {mesesInativo}+ meses <button onClick={() => setMesesInativo('')}>✕</button></span>}
                    {valorMin && <span className={styles.badge}>💰 Min: R$ {parseFloat(valorMin).toLocaleString('pt-BR')} <button onClick={() => setValorMin('')}>✕</button></span>}
                    {valorMax && <span className={styles.badge}>💰 Máx: R$ {parseFloat(valorMax).toLocaleString('pt-BR')} <button onClick={() => setValorMax('')}>✕</button></span>}
                    {estado && <span className={styles.badge}>🗺️ {estado} <button onClick={() => setEstado('')}>✕</button></span>}
                    {tipoPessoa && <span className={styles.badge}>🪪 {tipoPessoa === 'pf' ? 'CPF' : 'CNPJ'} <button onClick={() => setTipoPessoa('')}>✕</button></span>}
                    {tipoLead && <span className={styles.badge}>🏷️ {tipoLead} <button onClick={() => setTipoLead('')}>✕</button></span>}
                    {busca && <span className={styles.badge}>🔎 "{busca}" <button onClick={() => { setBusca(''); setBuscaInput(''); }}>✕</button></span>}
                    <span className={styles.badgeCount}>({pagination?.total || 0} resultados)</span>
                </div>
            )}

            {/* === Tabela de Clientes === */}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: 36 }}>#</th>
                            <th onClick={() => handleSort('nome')} className={ordenarPor === 'nome' ? styles.thActive : ''}>
                                Cliente{sortIcon('nome')}
                            </th>
                            <th>CPF/CNPJ</th>
                            <th>Grupo</th>
                            <th onClick={() => handleSort('churn_score')} className={ordenarPor === 'churn_score' ? styles.thActive : ''}>
                                Churn{sortIcon('churn_score')}
                            </th>
                            <th onClick={() => handleSort('dias_inativo')} className={ordenarPor === 'dias_inativo' ? styles.thActive : ''}>
                                Inativo{sortIcon('dias_inativo')}
                            </th>
                            <th onClick={() => handleSort('qtd_compras')} className={ordenarPor === 'qtd_compras' ? styles.thActive : ''}>
                                Compras{sortIcon('qtd_compras')}
                            </th>
                            <th onClick={() => handleSort('valor_total')} className={ordenarPor === 'valor_total' ? styles.thActive : ''}>
                                Total{sortIcon('valor_total')}
                            </th>
                            <th onClick={() => handleSort('ticket_medio')} className={ordenarPor === 'ticket_medio' ? styles.thActive : ''}>
                                Ticket{sortIcon('ticket_medio')}
                            </th>
                            <th>Última Compra</th>
                            <th style={{ width: 50 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!clientes || clientes.length === 0) ? (
                            <tr>
                                <td colSpan={11} style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
                                    Nenhum cliente encontrado
                                </td>
                            </tr>
                        ) : (
                            clientes.map((c, idx) => {
                                const cfg = GRUPOS_CONFIG[c.grupo_lifecycle] || GRUPOS_CONFIG.lost;
                                return (
                                    <tr key={c.cliente_id || idx}>
                                        <td className={styles.tdMuted}>{c.posicao}</td>
                                        <td>
                                            <div className={styles.clienteNome}>{c.nome}</div>
                                            {c.cidade && <div className={styles.clienteMeta}>📍 {c.cidade}{c.uf ? `/${c.uf}` : ''}</div>}
                                        </td>
                                        <td className={styles.tdMuted} style={{ fontSize: '0.72rem' }}>{c.cpf_cnpj || '—'}</td>
                                        <td>
                                            <span className={styles.grupoBadge} style={{ background: `${cfg.cor}18`, color: cfg.corTexto, border: `1px solid ${cfg.cor}40` }}>
                                                {cfg.emoji} {cfg.label}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.churnCell}>
                                                <div className={styles.churnMiniTrack}>
                                                    <div className={styles.churnMiniFill} style={{ width: `${c.churn_score}%`, background: getScoreColor(c.churn_score) }}></div>
                                                </div>
                                                <span style={{ color: getScoreColor(c.churn_score), fontWeight: 700, fontSize: '0.75rem' }}>{c.churn_score}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {c.dias_inativo !== null ? (
                                                <span style={{
                                                    fontWeight: 600,
                                                    color: c.dias_inativo > 120 ? '#dc3545' : c.dias_inativo > 60 ? '#fd7e14' : c.dias_inativo > 30 ? '#ffc107' : '#28a745'
                                                }}>
                                                    {c.dias_inativo}d
                                                </span>
                                            ) : (
                                                <span style={{ fontWeight: 600, color: '#6f42c1', fontSize: '0.75rem' }}>Nunca</span>
                                            )}
                                        </td>
                                        <td>
                                            {c.qtd_compras > 0 ? (
                                                <span
                                                    className={styles.comprasLink}
                                                    onClick={() => handleVerHistorico(c)}
                                                    title="Clique para ver pedidos"
                                                >
                                                    {c.qtd_compras}
                                                </span>
                                            ) : (
                                                <span style={{ fontWeight: 600 }}>{c.qtd_compras}</span>
                                            )}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>{fmtMoeda(c.valor_total)}</td>
                                        <td style={{ fontWeight: 600, color: '#28a745' }}>{fmtMoeda(c.ticket_medio)}</td>
                                        <td className={styles.tdMuted}>{fmtData(c.ultima_compra)}</td>
                                        <td>
                                            {c.telefone && (
                                                <button className={styles.actionBtn} onClick={() => abrirWhatsApp(c)} title="WhatsApp">📱</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* === Paginação Compacta === */}
            {pagination && pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                    <span className={styles.pageInfo}>
                        {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                    </span>
                    <div className={styles.pageBtns}>
                        <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
                        {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                            let pg;
                            if (pagination.totalPages <= 5) pg = i + 1;
                            else if (page <= 3) pg = i + 1;
                            else if (page >= pagination.totalPages - 2) pg = pagination.totalPages - 4 + i;
                            else pg = page - 2 + i;
                            return (
                                <button key={pg} className={`${styles.pageBtn} ${pg === page ? styles.pageBtnActive : ''}`} onClick={() => setPage(pg)}>
                                    {pg}
                                </button>
                            );
                        })}
                        <button className={styles.pageBtn} disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>→</button>
                    </div>
                </div>
            )}

            {/* Modal de Histórico */}
            <ClientHistoryModal
                show={showModal}
                onHide={handleCloseModal}
                cliente={selectedCliente}
                historico={historico}
                loading={loadingHistorico}
            />
        </div>
    );
};

export default LifecyclePanel;
